import React, { useRef } from 'react';
import { Alert, Box, Button, IconButton, Typography } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import CloseIcon from '@mui/icons-material/Close';

/**
 * EVIDENCE.1a — optional label photographs on the simple wine path.
 *
 * The capture mechanics are the scanner's, deliberately: `capture="environment"`
 * opens the rear camera directly on an iPad — the primary form factor — and
 * degrades to the OS file picker on desktop. No getUserMedia, no permission
 * ceremony, one control that works on both.
 *
 * The allowlist and the HEIC refusal are the same rules the server enforces,
 * and they are here for the same reason they are there: iPhone photos are HEIC
 * by default, no vision provider reads HEVC-encoded HEIC, and "your phone's
 * format isn't supported yet, here is the setting" is a different message from
 * "that isn't an image" to somebody standing in front of a bottle.
 *
 * NEVER REQUIRED. The name is the only field this path has ever demanded.
 * A photo is evidence for the reviewer's eyes — no AI, no parse, no cost.
 */

export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif';
export const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
export const PHOTO_CAP = 4;

export const isUnsupportedHeic = (file: File): boolean =>
  /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name);

export const HEIC_GUIDANCE =
  'iPhone photos are HEIC by default, which we cannot read yet. '
  + 'On the phone: Settings → Camera → Formats → Most Compatible, then retake — '
  + 'or share the photo as a JPEG.';

export interface CapturedPhoto { file: File; previewUrl: string }

interface Props {
  photos: CapturedPhoto[];
  onAdd: (file: File) => void;
  onRemove: (index: number) => void;
  disabled?: boolean;
  /** Set when a file was refused, so the reason sits next to the control. */
  error?: string | null;
}

const LabelPhotoCapture: React.FC<Props> = ({
  photos, onAdd, onRemove, disabled = false, error,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const atCap = photos.length >= PHOTO_CAP;

  return (
    <Box sx={{ mt: 2.5 }}>
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 0.5 }}>
        Label photo <Typography component="span" variant="caption" color="text.secondary">
          (optional)
        </Typography>
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        A photo helps the wine team identify this bottle. It is not read
        automatically — it is kept with the record.
      </Typography>

      {error && <Alert severity="warning" sx={{ mb: 1 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        {photos.map((p, i) => (
          <Box key={p.previewUrl} sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={p.previewUrl}
              alt={`Label photo ${i + 1}`}
              sx={{
                width: 64, height: 64, objectFit: 'cover',
                borderRadius: 1, border: 1, borderColor: 'divider',
              }}
            />
            <IconButton
              size="small"
              aria-label={`Remove label photo ${i + 1}`}
              onClick={() => onRemove(i)}
              disabled={disabled}
              sx={{
                position: 'absolute', top: -8, right: -8,
                bgcolor: 'background.paper', border: 1, borderColor: 'divider',
                '&:hover': { bgcolor: 'background.paper' },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}

        {!atCap && (
          <Button
            variant="outlined"
            startIcon={<PhotoCameraIcon />}
            onClick={() => inputRef.current?.click()}
            disabled={disabled}
            sx={{ textTransform: 'none', height: 64 }}
          >
            Add label photo
          </Button>
        )}
      </Box>

      {atCap && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          {PHOTO_CAP} photos is the maximum.
        </Typography>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        capture="environment"
        data-testid="label-photo-input"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Reset first: picking the same file twice must fire onChange again.
          e.target.value = '';
          if (file) onAdd(file);
        }}
      />
    </Box>
  );
};

export default LabelPhotoCapture;
